
/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { PermitAll } from '@csx/security';
import { debounceTime } from 'rxjs/operators';
import { LmsBaseAppComponent } from '../../core/component/base-app/lms-base-app.component';
import { LocomotiveErrors } from '../../core/model/locomotive-error.model';
import { DataStoreService } from '../../core/services/datastore.service';
import { PopupService } from '../../core/services/popup.service';
import { UserService } from '../../core/services/user.service';
import { LocoDetail } from './../../core/model/loco-detail.model';
import { AppUtilityService } from './../../core/services/app-utility.service';
import { ConfigService } from './../../core/services/config.service';
import { EditModeService } from './../../core/services/edit-mode.service';
import { LmsLoaderService } from './../../core/services/lms-loader.service';
import { LmsMenuService } from './../../core/services/lms-menu.service';
import { LmsModuleService } from './../../core/services/lms-module.service';
import { LmsRendererService } from './../../core/services/lms-renderer.service';
import { MessageHandlerService } from './../../core/services/message-handler.service';
import { RouteChangePopupService } from './../../core/services/route-change-popup.service';
import { StaticDataStoreService } from './../../core/services/static-datastore.service';
import { UtilService } from './../../core/services/util.service';
import { LocoNote } from './../../shared/model/loco-note.model';
import { NotesDetails } from './../../shared/model/notes-details.model';
import { NotesDataService } from './../../shared/services/note-data.service';
import { LocoDataService } from './../services/loco-data.service';


/**
 * @export
 * @class LmsLocoDataComponent
 * @extends {LmsBaseAppComponent}
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'lms-loco-data',
  templateUrl: './loco-data.component.html',
  styleUrls: ['./loco-data.component.scss']
})
@PermitAll()
export class LmsLocoDataComponent extends LmsBaseAppComponent implements OnInit, OnDestroy {

  loadData: LocoDetail;
  editData: LocoDetail;
  subTypes: string[];
  locoLabel: any;
  locoEditForm: UntypedFormGroup;
  updateResponse: LocomotiveErrors;
  errorMap: Map<string, boolean> = new Map<string, boolean>();
  listOfNote: Array<LocoNote> = [];
  notesMessage: string;
  upperHeight = 145;
  locoDataHeight: number;
  isLocoAddOpr = false;


  /**
   * Creates an instance of LmsLocoComponent.
   * @param {LocoDataService} locoDataService
   * @param {LmsModuleService} lmsModuleService
   * @param {ConfigService} configService
   * @param {LmsMenuService} lmsMenuService
   * @param {UtilService} utilService
   * @memberOf LmsLocoComponent
   */
  constructor(private locoDataService: LocoDataService,
    lmsModuleService: LmsModuleService, configService: ConfigService,
    lmsMenuService: LmsMenuService, utilService: UtilService,
    editModeService: EditModeService, private staticDataStoreService: StaticDataStoreService,
    private dataStoreService: DataStoreService,
    routeChangePopupService: RouteChangePopupService, private _fb: UntypedFormBuilder, private messageHandlerService: MessageHandlerService,
    popupService: PopupService, private appUtilityService: AppUtilityService, private lmsLoaderService: LmsLoaderService, private notesDataService: NotesDataService,
    private lmsRendererService: LmsRendererService, 
    public user: UserService) {
    super(configService, lmsMenuService, lmsModuleService, utilService, editModeService, routeChangePopupService, popupService);
    const configdata = this.configService.getConfigData();
    this.locoLabel = configdata.locoLabel;
    this.service = this.locoDataService;
    this.type = configdata.menu.type.locomotive;
    this.editData = {};
    this.createErrorMap();
    this.listOfNote = [];

    this.lmsRendererService.resizeObervable.pipe(debounceTime(10)).subscribe((isResize) => {
      if (isResize) {
        this.prepareHeightForPage();
      }
    });
  }

  prepareHeightForPage() {
    const windowHeight = this.lmsRendererService.getWindowHeight();
    this.locoDataHeight = windowHeight - this.upperHeight - 20;
  }


  /**
   * Set Edit Request Data
   * @param {*} locoEditForm
   * @memberOf LmsLocoDataComponent
   */
  setEditRequestData(locoEditForm: any) {
    this.editData.clazz = locoEditForm.value.editLoco[0].clazz;
    this.editData.directionFacing = locoEditForm.value.editLoco[0].directionFacing;
    this.editData.horsePower = locoEditForm.value.editLoco[0].horsePower;
    if (this.user && this.user.isUserManagerOrDirector) {
      this.editData.permanentTag = locoEditForm.value.editLoco[0].permanentTag ? locoEditForm.value.editLoco[0].permanentTag.toUpperCase() : '';
      this.editData.locoTag = locoEditForm.value.editLoco[0].locoTag ? locoEditForm.value.editLoco[0].locoTag.toUpperCase() : '';
    }
    if (this.loadData.permanentTag !== 'FL' &&
      (this.user && this.user.isUserManagerOrDirector)) {
      this.editData.rduInstalled = locoEditForm.value.editLoco[0].rduInstalled ? locoEditForm.value.editLoco[0].rduInstalled.toUpperCase() : '';
    }
    this.editData.fuelLevel = locoEditForm.value.editLoco[1].fuelLevel;
    this.editData.miles = locoEditForm.value.editLoco[1].miles;
    this.editData.capacity = locoEditForm.value.editLoco[1].capacity;
    if (this.loadData.permanentTag === 'FL') {
      this.editData.nextQDate = locoEditForm.value.editLoco[2].nextQDate;
      this.editData.nextDailyMIDueDate = locoEditForm.value.editLoco[2].nextDailyMIDueDate;
      this.editData.nextAirFlowDate = locoEditForm.value.editLoco[2].nextAirFlowDate;
      this.editData.rduInstalled = locoEditForm.value.editLoco[0].rduInstalled ? locoEditForm.value.editLoco[0].rduInstalled.toUpperCase() : '';
      this.editData.responsibleRoad = locoEditForm.value.editLoco[0].responsibleRoad ? locoEditForm.value.editLoco[0].responsibleRoad.toUpperCase() : '';
    }
    this.editData.remarks = locoEditForm.value.editLoco[0].remarks ? locoEditForm.value.editLoco[0].remarks.toUpperCase() : '';
  }
  /**
   * Update Sumbit function
   * @param {any} locoEditForm
   * @memberOf LmsLocoDataComponent
   */
  public saveEditableLocoData(locoEditForm) {
    if (this.locoEditForm && this.locoEditForm.valid) {
      this.lmsLoaderService.showLoader();
      this.createErrorMap();
      this.editData = {};
      this.setEditRequestData(locoEditForm);
      const origLocoId = this.appUtilityService.formatLocomotiveId(this.selectedTab.tabSearchParameter.searchText);
      this.locoDataService.updateLocomotive(this.editData, origLocoId).subscribe(
        (data) => {
          this.updateLocomotive(data);
        }, error => {
          this.errorUpdateLoco(error);
        });
    }
  }


  /**
   * This method used to show error on Loco Update
   * @param {*} error
   * @memberof LmsLocoDataComponent
   */
  errorUpdateLoco(error: any) {
    this.lmsLoaderService.hideLoader();
    this.selectedTab.subTypeDetail.data.message = this.appUtilityService.getHttpErroMessage(error, false);
    const editModeStatus = {
      status: false,
      message: ''
    };
    this.editModeService.setEditMode(editModeStatus);
    setTimeout(() => {
      this.lmsModuleService.updateSelectedTab(this.selectedTab);
    }, 10);
    setTimeout(() => {
      this.checkEditModeStatus();
    }, 15);
  }

  /**
   * This method update Locomotive
   * @param {*} data
   * @memberof LmsLocoDataComponent
   */
  updateLocomotive(data: any) {
    this.updateResponse = data;
    if (this.isNumeric(this.updateResponse.header.priorityCode)) {
      const pCode = parseInt(this.updateResponse.header.priorityCode, 10);
      if (pCode >= 1 && pCode <= 4) {
        this.updateNotes();
        this.listOfNote = [];
        this.selectedTab.mode = 'view';
        this.selectedTab.subTypeDetail[this.selectedTab.subType.toLowerCase()].status = 'refresh';
        this.checkEditModeStatus();
        setTimeout(() => {
          if (this.notesMessage) {
            this.updateResponse.header.messageText = this.updateResponse.header.messageText + ' & ' + this.notesMessage;
          } else {
            this.updateResponse.header.messageText = this.updateResponse.header.messageText;
          }
          this.locoDataService.load(this.selectedTab, true, this.updateResponse.header.messageText);
        }, 500);
      } else {
        this.lmsLoaderService.hideLoader();
        this.errorMap.forEach((value: boolean, key: string) => {
          if ('C' === this.updateResponse[key] || 'H' === this.updateResponse[key]) {
            this.errorMap.set(key, true);
          }
        });
        this.selectedTab.subTypeDetail.data.message = this.updateResponse.header.messageText;
        const editModeStatus = {
          status: false,
          message: ''
        };
        this.editModeService.setEditMode(editModeStatus);
        setTimeout(() => {
          this.lmsModuleService.updateSelectedTab(this.selectedTab);
        }, 10);
        setTimeout(() => {
          this.checkEditModeStatus();
        }, 15);
      }
    }
  }
  /**
     * This method update Notes
     * @param {any} event
     * @memberOf LmsNotesDataListComponent
     */
  public updateNotes() {
    const updatedNotesRequest = this.generateRequestForNotesUpdate(this.locoEditForm);
    if (updatedNotesRequest && updatedNotesRequest.listOfNotes && updatedNotesRequest.listOfNotes.length > 0) {
      let updateResponse: NotesDetails;
      this.notesDataService.updateNote(this.selectedTab, updatedNotesRequest, 'LWLD', 'L').subscribe(
        (data) => {
          updateResponse = <NotesDetails>data;
          if (this.isNumeric(updateResponse.priorityCode)) {
            const pCode = parseInt(updateResponse.priorityCode, 10);
            if (pCode >= 1 && pCode <= 4) {
              this.notesMessage = 'NOTES SUCCESSFULLY UPDATED';
            } else {
              this.notesMessage = 'NOTES UPDATE FAILED BECAUSE ' + updateResponse.messageText;
            }
          }
        }, error => {
          this.notesMessage = 'NOTES UPDATE FAILED';
        });
    } else {
      this.notesMessage = '';
    }

  }

  /**
   * Creating request for Update Notes
   * @param {*} locoEditForm
   * @returns {*}
   * @memberOf LmsLocoDataComponent
   */
  generateRequestForNotesUpdate(locoEditForm: any): any {
    const finalNoteList: Array<LocoNote> = [];
    for (let i = 0; i < this.listOfNote.length; i++) {
      for (let j = 3; j < locoEditForm.value.editLoco.length; j++) {
        if (i === j - 3) {
          if ('Y' !== this.listOfNote[i].isDeleted) {
            if (this.listOfNote[i].noteText !== locoEditForm.value.editLoco[j].noteText) {
              finalNoteList.push(this.getUpdatedNote(locoEditForm.value.editLoco[j].noteText));
            } else {
              finalNoteList.push(this.getUpdatedRemainingNote(this.listOfNote[i]));
            }
          }
        }
      }
    }
    return this.getUpdatedNotesList(finalNoteList);
  }
  /**
   * @private
   * @param {string} noteText
   * @returns
   * @memberOf LmsLocoDataComponent
   */
  private getUpdatedNote(noteText: string) {
    const note = {
      noteText: noteText ? noteText.toUpperCase() : ''
    };
    return note;
  }

  /**
  * @private
  * @param {Array<LocoNote>} notes
  * @returns
  * @memberOf LmsLocoDataComponent
  */
  private getUpdatedNotesList(notes: Array<LocoNote>) {
    const updatedNotes = {
      listOfNotes: notes
    };
    return updatedNotes;
  }

  /**
   * @private
   * @param {LocoNote} data
   * @returns
   * @memberOf LmsLocoDataComponent
   */
  private getUpdatedRemainingNote(data: LocoNote) {
    const note = {
      noteText: data.noteText ? data.noteText.toUpperCase() : '',
      timestamp: data.timestamp,
      userIdentifier: data.userIdentifier
    };
    return note;

  }

  /**
   * Creating Error Map
   * @memberOf LmsLocoDataComponent
   */
  createErrorMap() {
    this.errorMap.set('classError', false);
    this.errorMap.set('directionFacingError', false);
    this.errorMap.set('horsePowerError', false);
    this.errorMap.set('responsibleRoadError', false);
    this.errorMap.set('fuelLevelError', false);
    this.errorMap.set('milesError', false);
    this.errorMap.set('nextQDateError', false);
    this.errorMap.set('capacityError', false);
    this.errorMap.set('permanentTagError', false);
    this.errorMap.set('rduInstalledError', false);
    this.errorMap.set('locoTagError', false);
    this.errorMap.set('nextDailyMIDueDateError', false);
    this.errorMap.set('nextAirFlowDateError', false);
  }


  /**
   * Edit Locomotive Information Form
   * @param {LocoDetail} loco
   * @returns
   * @memberOf LmsLocoDataComponent
   */
  editableLocomotiveInformation(loco: LocoDetail) {
    return this._fb.group({
      clazz: loco.clazz,
      horsePower: loco.horsePower,
      responsibleRoad: loco.responsibleRoad,
      directionFacing: loco.directionFacing,
      permanentTag: loco.permanentTag,
      locoTag: loco.locoTag,
      rduInstalled: loco.rduInstalled,
      remarks: loco.remarks
    });
  }

  /**
   * Edit Fuel Form
   * @param {LocoDetail} loco
   * @returns
   * @memberOf LmsLocoDataComponent
   */
  editableFuelInformation(loco: LocoDetail) {
    return this._fb.group({
      fuelLevel: loco.fuelLevel,
      miles: loco.miles,
      capacity: loco.capacity
    });
  }

  /**
   * Edit note Sections
   * @param {LocoNote} note
   * @returns
   * @memberOf LmsLocoDataComponent
   */
  editableNotesInformation(note: LocoNote) {
    return this._fb.group({
      noteText: note.noteText
    });
  }
  /**
      * Show more list when show more is clicked
      * @memberOf LmsLocoNotesComponent
      */
  showMoreList() {
    this.loadData.shownList = this.loadData.listOfNote.length;
  }

  /**
    * Show less list when show less is clicked
    * @memberOf LmsLocoNotesComponent
    */
  showLessList() {
    this.loadData.shownList = 3;
  }
  /**
   * Edit QInspection Form
   * @param {LocoDetail} loco
   * @returns
   * @memberOf LmsLocoDataComponent
   */
  editableQinspsctionInformation(loco: LocoDetail) {
    return this._fb.group({
      nextQDate: loco.nextQDate,
      nextDailyMIDueDate: loco.nextDailyMIDueDate,
      nextAirFlowDate: loco.nextAirFlowDate
    });
  }
  /**
   * This is used to get data from service and setting the data into our UI used Models
   * @memberOf LmsLocoComponent
   */
  ngOnInit() {
    super.init(this.search);
    this.prepareHeightForPage();
  }

  /**
  * This method invoked when search for train information is performed.
  * @memberOf LmsTrainComponent
  */
  public search() {
    if (!this.isEditMode()) {
      this.selectedTab.mode = 'view';
      this.createErrorMap();
      this.updateTab(this.selectedTab);
    }
    this.locoDataService.load(this.selectedTab);
  }

  public addLocoInitiated(event) {
    this.isLocoAddOpr = true;
  }

  /**
   * This method sorts the loco notes according to date
   * @param {Array<LocoNote>} listOfNote
   * @returns {Array<LocoNote>}
   * @memberOf LmsLocoDataComponent
   */
  sortedNotesList(listOfNote: Array<LocoNote>): Array<LocoNote> {
    if (!listOfNote) {
      return listOfNote;
    }
    listOfNote.sort((a: LocoNote, b: LocoNote) => {
      if (new Date(a.timestamp) < new Date(b.timestamp)) {
        return 1;
      }
      if (new Date(a.timestamp) > new Date(b.timestamp)) {
        return -11;
      }
      if (new Date(a.timestamp) === new Date(b.timestamp)) {
        return 0;
      }
    });
    return listOfNote;
  }

  /**
   * This method is used to unsubscribe the subscribed Observables.
   * @memberOf LmsLocoComponent
   */
  ngOnDestroy() {
    super.destroy();
  }

  /**
  * Remove error when error message is closed
  * @param {any} event
  * @memberOf LmsLocoComponent
  */
  clearErrorMessage(event) {
    this.selectedTab.subTypeDetail.data.message = '';
    const editModeStatus = {
      status: false,
      message: ''
    };
    this.editModeService.setEditMode(editModeStatus);
    setTimeout(() => {
      this.lmsModuleService.updateSelectedTab(this.selectedTab);
    }, 5);
    setTimeout(() => {
      this.checkEditModeStatus();
    }, 10);
  }

  /**
    * @private
    * @memberOf LmsLocoTopActionsComponent
    */
  private checkEditModeStatus() {
    if (this.selectedTab && this.selectedTab.subTypeDetail[this.selectedTab.subType.toLowerCase()].status === 'loaded' && this.selectedTab.mode === 'edit') {
      const editModeStatus = {
        status: true,
        message: 'Unsaved data will be lost. Do you want to continue?'
      };
      this.editModeService.setEditMode(editModeStatus);
    } else {
      const editModeStatus = {
        status: false,
        message: ''
      };
      this.editModeService.setEditMode(editModeStatus);
    }
  }

  /**
   * Edit Loco Details
   * @param {any} event
   * @memberOf LmsLocoDataComponent
   */
  public editLocomotiveDetails(event) {
    this.listOfNote = this.loadData.listOfNote;
    if (event) {
      this.selectedTab = event;
    }
    this.updateTab(this.selectedTab);
    this.createErrorMap();
    this.locoEditForm = this._fb.group({
      editLoco: this._fb.array([])
    });
    const control = <UntypedFormArray>this.locoEditForm.controls['editLoco'];
    control.push(this.editableLocomotiveInformation(this.loadData));
    control.push(this.editableFuelInformation(this.loadData));
    control.push(this.editableQinspsctionInformation(this.loadData));
    for (const note of this.loadData.listOfNote) {
      control.push(this.editableNotesInformation(note));
    }
  }

}
